import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Button, Dropdown, Spinner } from 'react-bootstrap';
import { 
  FaCalendarAlt, FaDatabase, FaUserCheck, FaGoogleDrive, FaEnvelope, 
  FaUsers, FaClock, FaMapMarkerAlt, FaPlus, FaTimes, FaCheckCircle,
  FaPencilAlt, FaStar, FaInfoCircle
} from 'react-icons/fa';
import { PiBackpackBold } from 'react-icons/pi';
import { BiCommentDetail } from 'react-icons/bi';
import { RiDirectionLine } from 'react-icons/ri';
import { collection, getDocs, setDoc, doc, addDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import './Dashboard.css';

const CALENDAR_ID = 'c_g1fmt6s44qqhil9aib1pcf3r4s@group.calendar.google.com';
const API_KEY = 'AIzaSyC6HDB44mLIJeF8UlWhW-xBV-7BZaeWjH0';

const formatDate = (dateString) => {
  if (!dateString) return '';
  const parts = dateString.split('-');
  return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : dateString;
};

const formatEventDateTime = (event) => {
  if (event.start) {
    if (event.start.dateTime) return new Date(event.start.dateTime).toLocaleString('de-CH', { timeZone: 'Europe/Zurich', dateStyle: 'short', timeStyle: 'short' });
    if (event.start.date) return new Date(event.start.date).toLocaleDateString('de-CH', { timeZone: 'Europe/Zurich' });
  }
  return '';
};

const formatEventEndDateTime = (event) => {
  if (event.end) {
    if (event.end.dateTime) return new Date(event.end.dateTime).toLocaleString('de-CH', { timeZone: 'Europe/Zurich', dateStyle: 'short', timeStyle: 'short' });
    if (event.end.date) return new Date(event.end.date).toLocaleDateString('de-CH', { timeZone: 'Europe/Zurich' });
  }
  return '';
};

const quickAccessLinks = [
  { href: 'https://inventar.pfadi-sirius.ch', label: 'Mat-DB', icon: FaDatabase, color: 'teal' },
  { href: '/AnAbmeldungen', label: 'An/Abmeldungen', icon: FaUserCheck, color: 'coral' },
  { href: 'https://drive.google.com', label: 'Google Drive', icon: FaGoogleDrive, color: 'navy' },
  { href: 'https://mail.google.com', label: 'Gmail', icon: FaEnvelope, color: 'olive' }
];

const QuickAccessLinkComponent = React.memo(({ href, label, icon: Icon, color }) => {
  const newTab = label !== 'An/Abmeldungen';
  return (
    <a
      href={href}
      {...(newTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      className={`quick-access-link ${color}`}
    >
      <Icon className="button-icon" />
      <span className="button-label">{label}</span>
    </a>
  );
});

const getNextSaturday = () => {
  const today = new Date();
  const result = new Date(today);
  result.setDate(today.getDate() + ((6 - today.getDay() + 7) % 7 || 7));
  const yyyy = result.getFullYear();
  const mm = String(result.getMonth() + 1).padStart(2, '0');
  const dd = String(result.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const timeOptions = [
  "08:00", "08:30", "09:00", "09:30", "10:00", "10:30", 
  "11:00", "11:30", "12:00", "12:30", "13:00", "13:30", 
  "14:00", "14:30", "15:00", "15:30", "16:00", "16:30",
  "17:00", "17:30", "18:00", "18:30", "19:00", "19:30",
  "20:00", "20:30", "21:00", "21:30", "22:00", "22:30",
  "23:00", "23:30", "00:00"
];

const GroupDropdown = React.memo(({ options, selected, onChange, label }) => {
  const toggleOption = useCallback((optionName) => {
    const newSelected = selected.includes(optionName)
      ? selected.filter(item => item !== optionName)
      : [...selected, optionName];
    onChange(newSelected);
  }, [selected, onChange]);

  return (
    <Dropdown autoClose="outside" className="custom-dropdown">
      <Dropdown.Toggle variant="outline-secondary" className="custom-dropdown-toggle modern-btn">
        <FaUsers className="me-2" />
        {label}: {selected.length ? selected.join(', ') : 'Bitte auswählen'}
      </Dropdown.Toggle>
      <Dropdown.Menu className="custom-dropdown-menu">
        {options.map(option => (
          <Dropdown.Item key={option.id} onClick={() => toggleOption(option.name)}>
            {option.name} {selected.includes(option.name) && <FaTimes size={10} className="ms-1" />}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
});

const TreffpunktDropdown = React.memo(({ label, available, selected, onSelect, onNewTreffpunkt }) => {
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newLink, setNewLink] = useState('');

  const handleSelect = useCallback((title) => onSelect(title), [onSelect]);

  const handleConfirm = useCallback(async () => {
    if (newTitle && newLink) {
      const newTp = { title: newTitle, googleMapsLink: newLink };
      try {
        const docRef = await addDoc(collection(db, 'treffpunkte'), newTp);
        newTp.id = docRef.id;
        onNewTreffpunkt(newTp);
        onSelect(newTitle);
        setNewTitle('');
        setNewLink('');
        setShowNew(false);
      } catch (error) {
        console.error('Fehler beim Hinzufügen des Treffpunkts:', error);
      }
    }
  }, [newTitle, newLink, onNewTreffpunkt, onSelect]);

  return (
    <Dropdown className="custom-dropdown" autoClose="inside">
      <Dropdown.Toggle variant="outline-secondary" className="custom-dropdown-toggle modern-btn">
        <FaMapMarkerAlt className="me-2" />
        {label}: {selected || 'Bitte wählen'}
      </Dropdown.Toggle>
      <Dropdown.Menu className="custom-dropdown-menu">
        {available.map(tp => (
          <Dropdown.Item key={tp.id} onClick={() => handleSelect(tp.title)}>
            {tp.title}
          </Dropdown.Item>
        ))}
        <Dropdown.Divider />
        <Dropdown.Item
          as="div"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowNew(prev => !prev);
          }}
        >
          <FaPlus className="me-1" /> Neuen Treffpunkt hinzufügen
        </Dropdown.Item>
        {showNew && (
          <div className="p-2">
            <Form.Control
              type="text"
              placeholder="Titel"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="mb-2 modern-input"
            />
            <Form.Control
              type="url"
              placeholder="Google Maps Link"
              value={newLink}
              onChange={(e) => setNewLink(e.target.value)}
              className="mb-2 modern-input"
            />
            <Button variant="primary" size="sm" onClick={handleConfirm} className="modern-btn">
              Bestätigen
            </Button>
          </div>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
});

const MitnehmenDropdown = React.memo(({ available, selected, onChange }) => {
  const [customInput, setCustomInput] = useState('');
  const toggleOption = useCallback((option) => {
    const newSelected = selected.includes(option)
      ? selected.filter(item => item !== option)
      : [...selected, option];
    onChange(newSelected);
  }, [selected, onChange]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmed = customInput.trim();
      if (trimmed && !selected.includes(trimmed)) {
        onChange([...selected, trimmed]);
        setCustomInput('');
      }
    }
  }, [customInput, selected, onChange]);

  return (
    <Dropdown autoClose="outside" className="custom-dropdown">
      <Dropdown.Toggle 
        variant="outline-secondary" 
        className="custom-dropdown-toggle modern-btn mitnehmen-toggle"
      >
        <span className="mitnehmen-label">Mitnehmen:</span>{" "}
        <span className="mitnehmen-selected">
          {selected.length ? selected.join(', ') : 'Bitte auswählen'}
        </span>
      </Dropdown.Toggle>
      <Dropdown.Menu className="custom-dropdown-menu">
        {selected.length > 0 && (
          <div className="px-3 pb-2">
            <div className="fw-bold">Ausgewählte Optionen:</div>
            {selected.map((item, idx) => (
              <div key={idx} className="d-flex align-items-center justify-content-between">
                <span>{item}</span>
                <Button variant="link" size="sm" onClick={() => toggleOption(item)}>
                  <FaTimes />
                </Button>
              </div>
            ))}
            <Dropdown.Divider />
          </div>
        )}
        {available.map(option => (
          <Dropdown.Item key={option} onClick={() => toggleOption(option)}>
            {option} {selected.includes(option) && <FaTimes size={10} className="ms-1" />}
          </Dropdown.Item>
        ))}
        <Dropdown.Divider />
        <div className="px-3">
          <Form.Control
            type="text"
            placeholder="Eigene Option eingeben und Enter drücken"
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="modern-input"
          />
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
});

// NextEventBox-Komponente: Abruf des nächsten Events nur einmal beim Mounting (kein Intervall)
const NextEventBox = React.memo(() => {
  const [nextEvent, setNextEvent] = useState(null);
  const [nextEventLoading, setNextEventLoading] = useState(false);
  const [nextEventError, setNextEventError] = useState(null);

  useEffect(() => {
    const fetchNextEvent = async () => {
      setNextEventLoading(true);
      setNextEventError(null);
      try {
        const now = new Date().toISOString();
        const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(CALENDAR_ID)}/events?key=${API_KEY}&timeMin=${now}&singleEvents=true&orderBy=startTime&maxResults=1`;
        const response = await fetch(url);
        const data = await response.json();
        if (data.error) {
          setNextEventError(data.error.message);
        } else {
          setNextEvent(data.items && data.items.length > 0 ? data.items[0] : null);
        }
      } catch (err) {
        console.error('Fehler beim Laden des nächsten Events:', err);
        setNextEventError('Fehler beim Laden des nächsten Events.');
      }
      setNextEventLoading(false);
    };
    fetchNextEvent();
  }, []);

  return (
    <Card className="modern-card next-event-box">
      <Card.Body style={{ padding: '0.5rem 0.75rem' }}>
        {nextEventLoading ? (
          <div className="d-flex justify-content-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Laden...</span>
            </Spinner>
          </div>
        ) : nextEventError ? (
          <p className="text-danger text-center" style={{ fontSize: '0.85rem' }}>{nextEventError}</p>
        ) : nextEvent ? (
          <>
            <p style={{ fontSize: '0.75rem', margin: '0 0 0.25rem 0', color: '#777' }}>nächster Anlass</p>
            <h3>{nextEvent.summary || 'Kein Titel'}</h3>
            <p>
              <FaCalendarAlt className="me-1" />
              <strong>Datum:</strong> {formatEventDateTime(nextEvent)} {nextEvent.end && `- ${formatEventEndDateTime(nextEvent)}`}
            </p>
            {nextEvent.location && (
              <p>
                <FaMapMarkerAlt className="me-1" />
                <strong>Ort:</strong> {nextEvent.location}
              </p>
            )}
            {nextEvent.description && (
              <p>
                <FaInfoCircle className="me-1" />
                <strong>Info:</strong> {nextEvent.description}
              </p>
            )}
          </>
        ) : (
          <p className="text-center" style={{ fontSize: '0.85rem', color: '#777' }}>
            Keine anstehenden Events gefunden.
          </p>
        )}
      </Card.Body>
    </Card>
  );
});

const Dashboard = ({ user }) => {
  const [activityType, setActivityType] = useState('normale aktivitaet');
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [besammlungsDatum, setBesammlungsDatum] = useState(getNextSaturday());
  const [besammlungsUhrzeit, setBesammlungsUhrzeit] = useState('14:00');
  const [verabschiedungsDatum, setVerabschiedungsDatum] = useState(getNextSaturday());
  const [verabschiedungsUhrzeit, setVerabschiedungsUhrzeit] = useState('16:00');
  const [groups, setGroups] = useState([]);
  const [availableTreffpunkte, setAvailableTreffpunkte] = useState([]);
  const [selectedTreffpunktBes, setSelectedTreffpunktBes] = useState('');
  const [selectedTreffpunktVerabsch, setSelectedTreffpunktVerabsch] = useState('');
  const [selectedMitnehmen, setSelectedMitnehmen] = useState([]);
  const availableMitnehmen = ['Zvieri', 'Ztrinke', 'dem Wetter angepasste Kleidung', 'Sackmesser', 'Uniform', 'Krawatte', 'gutes Schuhwerk', 'Wanderschuhe'];
  const [kommentar, setKommentar] = useState('');
  const [step, setStep] = useState('form');
  const [result, setResult] = useState({ success: null, message: '' });

  const resetForm = useCallback(() => {
    setActivityType('normale aktivitaet');
    setSelectedGroups([]);
    setBesammlungsDatum(getNextSaturday());
    setBesammlungsUhrzeit('14:00');
    setVerabschiedungsDatum(getNextSaturday());
    setVerabschiedungsUhrzeit('16:00');
    setSelectedTreffpunktBes('');
    setSelectedTreffpunktVerabsch('');
    setSelectedMitnehmen([]);
    setKommentar('');
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const groupsSnap = await getDocs(collection(db, 'groups'));
        const groupsData = groupsSnap.docs.map(doc => ({ id: doc.id, name: doc.data().name }));
        setGroups(groupsData);
      } catch (error) {
        console.error('Fehler beim Laden der Gruppen:', error);
      }
      try {
        const tpSnap = await getDocs(collection(db, 'treffpunkte'));
        const tpData = tpSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAvailableTreffpunkte(tpData);
      } catch (error) {
        console.error('Fehler beim Laden der Treffpunkte:', error);
      }
    };
    fetchData();
  }, []);

  const handleSelectTreffpunktBes = useCallback((title) => {
    setSelectedTreffpunktBes(title);
    setSelectedTreffpunktVerabsch(title);
  }, []);

  const clearRegistrations = useCallback(async (groupId) => {
    const regsRef = collection(db, 'chastlizettel', groupId, 'registrations');
    const regsSnap = await getDocs(regsRef);
    await Promise.all(regsSnap.docs.map(docSnap => deleteDoc(doc(db, 'chastlizettel', groupId, 'registrations', docSnap.id))));
  }, []);

  const publishChastlizettel = useCallback(async () => {
    const verabschiedungsTimestamp = new Date(`${verabschiedungsDatum}T${verabschiedungsUhrzeit}:00`);
    const chastlizettelData = {
      activityType,
      selectedGroups,
      besammlungsDatum,
      besammlungsUhrzeit,
      verabschiedungsDatum,
      verabschiedungsUhrzeit,
      verabschiedungsTimestamp,
      treffpunktBes: selectedTreffpunktBes,
      treffpunktVerabsch: selectedTreffpunktVerabsch,
      selectedMitnahmen: selectedMitnehmen,
      kommentar,
      timestamp: new Date(),
      aktivitaetsDatum: besammlungsDatum
    };

    try {
      for (const groupId of selectedGroups) {
        await clearRegistrations(groupId);
        await setDoc(doc(db, 'chastlizettel', groupId), chastlizettelData);
      }
      setResult({ success: true, message: 'Das Chastlizettel wurde erfolgreich publiziert!' });
      resetForm();
      setStep('result');
    } catch (error) {
      console.error('Fehler beim Publizieren:', error);
      setResult({ success: false, message: 'Fehler beim Publizieren des Chastlizettels.' });
      setStep('result');
    }
  }, [
    selectedGroups, besammlungsDatum, besammlungsUhrzeit, verabschiedungsDatum,
    verabschiedungsUhrzeit, selectedTreffpunktBes, selectedTreffpunktVerabsch,
    selectedMitnehmen, kommentar, clearRegistrations, resetForm
  ]);

  const handlePreview = useCallback((e) => {
    e.preventDefault();
    if (!selectedGroups.length) {
      alert('Bitte mindestens eine Gruppe auswählen.');
      return;
    }
    if (activityType !== 'keine aktivitaet') {
      if (!selectedTreffpunktBes || !selectedTreffpunktVerabsch) {
        alert('Bitte einen Treffpunkt für Besammlung und Verabschiedung auswählen.');
        return;
      }
      if (!selectedMitnehmen.length) {
        alert('Bitte mindestens eine Option bei "Mitnehmen" auswählen.');
        return;
      }
    }
    setStep('preview');
  }, [selectedGroups, activityType, selectedTreffpunktBes, selectedTreffpunktVerabsch, selectedMitnehmen]);

  const handleBackToForm = useCallback(() => setStep('form'), []);

  const inlineStyles = useMemo(() => `
    :root {
      --primary-color: rgb(81,125,152);
      --secondary-color: #50E3C2;
      --accent-color-1: #FF6B6B;
      --accent-color-2: #1E3D59;
      --accent-color-3: #3D9970;
      --bg-gradient: linear-gradient(135deg, rgba(240,248,255,0.2), rgba(230,247,255,0.2));
      --card-bg: rgba(255, 255, 255, 0.6);
      --shadow-color: rgba(0, 0, 0, 0.1);
      --btn-back-color: #ff9800;
      --background-color: rgb(243,242,241);
    }
    body { 
      background: var(--background-color); 
      backdrop-filter: blur(4px);
      font-family: 'Roboto', sans-serif;
      color: #333;
      margin: 0;
      padding: 0;
    }
    .modern-card {
      border: none;
      border-radius: 16px;
      box-shadow: 0 8px 32px var(--shadow-color);
      padding: 1.5rem;
      background: var(--card-bg);
      backdrop-filter: blur(10px);
    }
    .modern-input, .modern-select {
      border-radius: 8px;
      border: 1px solid #ccc;
      padding: 0.75rem;
      font-size: 1rem;
      background: rgba(255,255,255,0.8);
      transition: border-color 0.3s ease;
      appearance: none;
    }
    .modern-input:focus, .modern-select:focus {
      border-color: var(--primary-color);
      outline: none;
    }
    .modern-btn {
      border-radius: 8px;
      font-weight: bold;
      padding: 0.6rem 1.2rem;
      background-color: var(--primary-color);
      border: none;
      color: #fff;
      transition: background-color 0.3s ease;
    }
    .modern-btn:hover {
      background-color: rgba(81,125,152,0.85);
    }
    .preview-btn {
      border: 2px solid var(--primary-color);
      background: transparent;
      color: var(--primary-color);
    }
    .quick-access-link {
      transition: transform 0.2s;
    }
    .quick-access-link:hover {
      transform: scale(1.05);
    }
    .form-title {
      font-weight: bold;
      font-size: 1.5rem;
    }
    .next-event-box {
      max-width: 600px;
      margin: 0 auto 1rem auto;
      font-size: 0.9rem;
      padding: 0.75rem 1rem;
      background: var(--bg-gradient);
      border-radius: 16px;
    }
    .next-event-box h3 {
      font-size: 1.2rem;
      margin-bottom: 0.3rem;
      color: var(--primary-color);
      font-weight: bold;
    }
    .next-event-box p {
      margin: 0.2rem 0;
      color: #555;
    }
    .quick-access-container a.quick-access-link {
      flex: 1;
      margin: 0 2px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      font-size: 0.8rem;
      background-color: transparent !important;
      text-align: center;
      height: 60px;
      max-width: 80px;
    }
    .quick-access-container a.quick-access-link .button-icon {
      font-size: 1.2rem;
    }
    .quick-access-container a.quick-access-link .button-label {
      margin-top: 2px;
      font-size: 0.8rem;
      text-align: center;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    @media (max-width: 767px) {
      input[type="date"]::-webkit-datetime-edit { display: flex; }
      input[type="date"]::-webkit-datetime-edit-day-field { order: 1; }
      input[type="date"]::-webkit-datetime-edit-month-field { order: 2; }
      input[type="date"]::-webkit-datetime-edit-year-field { order: 3; }
      .mitnehmen-toggle {
        width: 150px;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }
      .custom-dropdown-menu { width: auto; }
      .custom-dropdown-menu .dropdown-item {
        font-size: 0.9rem;
        padding: 0.5rem 1rem;
      }
      .quick-access-container a.quick-access-link .button-icon,
      .quick-access-container a.quick-access-link .button-label {
        color: var(--primary-color) !important;
      }
      .modern-card { margin-bottom: 1rem !important; }
    }
    @media (max-width: 576px) {
      .form-title { font-size: 1.3rem; }
      .modern-card { padding: 0.75rem; }
      .modern-input, .modern-select { font-size: 0.9rem; padding: 0.6rem; }
    }
    input[type="date"] {
      cursor: pointer;
      padding: 1rem;
      font-size: 1rem;
      border: none;
      background: rgba(255,255,255,0.8);
      border-radius: 8px;
      transition: background 0.3s ease;
    }
    input[type="date"]:focus {
      background: #fff;
      outline: none;
    }
  `, []);

  return (
    <div className="dashboard-page" style={{ backgroundColor: 'rgb(243,242,241)', minHeight: '100vh', paddingBottom: '70px' }}>
      <style>{inlineStyles}</style>
      
      {step === 'form' && (
        <Container className="dashboard-container mt-4 pb-5">
          <Row className="mb-4">
            <Col>
              <div className="hero-section modern-card">
                <h1 className="welcome-message">
                  Willkommen zurück,<br />{user ? user.name : 'Gast'}!
                </h1>
              </div>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col xs={12}>
              <NextEventBox />
            </Col>
          </Row>
          <div className="d-md-none mb-2">
            <Card className="modern-card" style={{ padding: '0.5rem' }}>
              <div className="quick-access-container">
                {quickAccessLinks.map(link => (
                  <QuickAccessLinkComponent key={link.label} {...link} />
                ))}
              </div>
            </Card>
          </div>
          <Row className="mb-4">
            <Col xs={12}>
              <Card className="modern-card">
                <Card.Body>
                  <h2 className="mb-3 form-title" style={{ color: 'var(--primary-color)' }}>
                    <FaPencilAlt style={{ fontSize: '1em', verticalAlign: 'middle', marginRight: '0.5rem' }} /> Aktivität planen
                  </h2>
                  <Form onSubmit={handlePreview}>
                    <Row className="mb-3">
                      <Col xs={12} md={6}>
                        <Form.Group controlId="activityType">
                          <Form.Label><FaCalendarAlt className="me-2" /> Art der Aktivität</Form.Label>
                          <Form.Select value={activityType} onChange={(e) => setActivityType(e.target.value)} className="modern-select">
                            <option value="normale aktivitaet">Normale Aktivität</option>
                            <option value="anlass">Anlass</option>
                            <option value="keine aktivitaet">Keine Aktivität</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6} className="d-flex align-items-end mt-3 mt-md-0">
                        <GroupDropdown options={groups} selected={selectedGroups} onChange={setSelectedGroups} label="Gruppe(n)" />
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={12} md={6}>
                        <Form.Group className="mb-3" controlId="besammlungsDatum">
                          <Form.Label><FaCalendarAlt className="me-2" /> Besammlungsdatum</Form.Label>
                          <Form.Control type="date" value={besammlungsDatum} onChange={(e) => setBesammlungsDatum(e.target.value)} className="modern-input" />
                        </Form.Group>
                        {activityType !== 'keine aktivitaet' && (
                          <>
                            <Form.Group className="mb-3" controlId="besammlungsUhrzeit">
                              <Form.Label><FaClock className="me-2" /> Besammlungsuhrzeit</Form.Label>
                              <Form.Select value={besammlungsUhrzeit} onChange={(e) => setBesammlungsUhrzeit(e.target.value)} className="modern-select" required>
                                {timeOptions.map(time => <option key={time} value={time}>{time}</option>)}
                              </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="treffpunktBes">
                              <Form.Label><FaMapMarkerAlt className="me-2" /> Treffpunkt (Besammlung) *</Form.Label>
                              <TreffpunktDropdown 
                                label="Standort" 
                                available={availableTreffpunkte} 
                                selected={selectedTreffpunktBes} 
                                onSelect={handleSelectTreffpunktBes}
                                onNewTreffpunkt={(newTp) => setAvailableTreffpunkte([...availableTreffpunkte, newTp])}
                              />
                            </Form.Group>
                          </>
                        )}
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group className="mb-3" controlId="verabschiedungsDatum">
                          <Form.Label><FaCalendarAlt className="me-2" /> Verabschiedungsdatum</Form.Label>
                          <Form.Control type="date" value={verabschiedungsDatum} onChange={(e) => setVerabschiedungsDatum(e.target.value)} className="modern-input" />
                        </Form.Group>
                        {activityType !== 'keine aktivitaet' && (
                          <>
                            <Form.Group className="mb-3" controlId="verabschiedungsUhrzeit">
                              <Form.Label><FaClock className="me-2" /> Verabschiedungsuhrzeit</Form.Label>
                              <Form.Select value={verabschiedungsUhrzeit} onChange={(e) => setVerabschiedungsUhrzeit(e.target.value)} className="modern-select" required>
                                {timeOptions.map(time => <option key={time} value={time}>{time}</option>)}
                              </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="treffpunktVerabsch">
                              <Form.Label><FaMapMarkerAlt className="me-2" /> Treffpunkt (Verabschiedung) *</Form.Label>
                              <TreffpunktDropdown 
                                label="Standort" 
                                available={availableTreffpunkte} 
                                selected={selectedTreffpunktVerabsch} 
                                onSelect={setSelectedTreffpunktVerabsch}
                                onNewTreffpunkt={(newTp) => setAvailableTreffpunkte([...availableTreffpunkte, newTp])}
                              />
                            </Form.Group>
                          </>
                        )}
                      </Col>
                    </Row>
                    {activityType !== 'keine aktivitaet' && (
                      <Row>
                        <Col xs={12}>
                          <Form.Group className="mb-3" controlId="mitnehmen">
                            <Form.Label><FaUsers className="me-2" /> Mitnehmen *</Form.Label>
                            <MitnehmenDropdown available={availableMitnehmen} selected={selectedMitnehmen} onChange={setSelectedMitnehmen} />
                            <Form.Text className="text-muted">Mehrfachauswahl möglich – eigene Optionen per Enter hinzufügen.</Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>
                    )}
                    <Row>
                      <Col xs={12}>
                        <Form.Group className="mb-3" controlId="kommentar">
                          <Form.Label><FaEnvelope className="me-2" /> Kommentar</Form.Label>
                          <Form.Control as="textarea" rows={3} placeholder="Optionaler Kommentar" value={kommentar} onChange={(e) => setKommentar(e.target.value)} className="modern-input" />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Button variant="outline-primary" type="submit" className="modern-btn preview-btn">
                      Vorschau
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      )}

      {step === 'preview' && (
        <Container className="mt-4">
          {activityType.toLowerCase() === 'keine aktivitaet' ? (
            <>
              <div className="chast-header">
                <div className="chast-header-content">
                  <FaTimes className="header-icon" />
                  <span className="header-title">Keine Aktivität</span>
                  <span className="header-date-small">{formatDate(besammlungsDatum)}</span>
                </div>
              </div>
              <Card className="chast-card modern-card">
                <Card.Body>
                  <div className="no-activity-box">
                    <div className="no-activity-icon"><FaTimes /></div>
                    <div className="no-activity-text">Diesen Samstag findet keine Aktivität statt</div>
                    {kommentar && <div className="no-activity-comment">{kommentar}</div>}
                  </div>
                </Card.Body>
              </Card>
            </>
          ) : (
            <>
              <div className="chast-header">
                <div className="chast-header-content">
                  {activityType.toLowerCase() === 'anlass' ? <FaStar className="header-icon" /> : activityType === 'planung' ? <FaPencilAlt className="header-icon" /> : <FaCalendarAlt className="header-icon" />}
                  <span className="header-title">
                    {activityType.toLowerCase() === 'anlass'
                      ? 'ANLASS'
                      : activityType === 'planung'
                      ? 'Chastlizettel'
                      : 'AKTIVITÄT'}
                  </span>
                  {activityType !== 'planung' && besammlungsDatum && (
                    <>
                      {besammlungsDatum !== verabschiedungsDatum ? (
                        <span className="header-date">{formatDate(besammlungsDatum)} – {formatDate(verabschiedungsDatum)}</span>
                      ) : (
                        <span className="header-date">{formatDate(besammlungsDatum)}</span>
                      )}
                    </>
                  )}
                </div>
              </div>
              <Card className="chast-card modern-card">
                <Card.Body>
                  {besammlungsDatum && verabschiedungsDatum && besammlungsDatum !== verabschiedungsDatum && (
                    <div className="timeline-dates-wrapper">
                      <div className="time-start-date-box">{formatDate(besammlungsDatum)}</div>
                      <div className="time-end-date-box">{formatDate(verabschiedungsDatum)}</div>
                    </div>
                  )}
                  <div className="chast-timeline">
                    <div className="timeline-bar">
                      <div className="time-section">
                        <FaClock className="clock-icon" />
                        <span className="time-start">{besammlungsUhrzeit || '14:00'}</span>
                      </div>
                      <div className="time-section">
                        <FaClock className="clock-icon" />
                        <span className="time-end">{verabschiedungsUhrzeit || '16:00'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="location-row">
                    <div className="location-field">
                      <div className="field-title"><FaMapMarkerAlt className="location-title-icon" /></div>
                      <a href="#" className="location-box">
                        <span>{selectedTreffpunktBes || 'Sirihuus'}</span>
                        <RiDirectionLine className="direction-icon" />
                      </a>
                    </div>
                    {selectedTreffpunktVerabsch && selectedTreffpunktVerabsch !== selectedTreffpunktBes && (
                      <div className="location-field">
                        <div className="field-title"><FaMapMarkerAlt className="location-title-icon" /></div>
                        <a href="#" className="location-box">
                          <span>{selectedTreffpunktVerabsch}</span>
                          <RiDirectionLine className="direction-icon" />
                        </a>
                      </div>
                    )}
                  </div>
                  <div className="mitnehmen-field">
                    <div className="field-title"><PiBackpackBold className="mitnehmen-icon" /></div>
                    <div className="items-container">
                      {selectedMitnehmen.length > 0 ? (
                        selectedMitnehmen.map((item, idx) => <span key={idx} className="item-box">{item}</span>)
                      ) : (
                        <span className="item-box">Zvieri, dem Wetter angepasste Kleidung</span>
                      )}
                    </div>
                  </div>
                  {kommentar && (
                    <div className="comment-section">
                      <div className="field-title"><BiCommentDetail className="comment-icon" /></div>
                      <div className="comment-box">{kommentar}</div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </>
          )}
          <Row className="mt-3">
            <Col xs={12} className="d-flex justify-content-between">
              <Button onClick={handleBackToForm} className="modern-btn">Zurück zum Formular</Button>
              <Button variant="success" onClick={publishChastlizettel} className="modern-btn">Publizieren</Button>
            </Col>
          </Row>
        </Container>
      )}

      {step === 'result' && (
        <Container className="mt-4">
          <Row className="mb-4">
            <Col className="text-center">
              {result.success ? (
                <>
                  <FaCheckCircle size={48} className="text-success" />
                  <h3 className="mt-3">{result.message}</h3>
                </>
              ) : (
                <>
                  <FaTimes size={48} className="text-danger" />
                  <h3 className="mt-3">{result.message}</h3>
                </>
              )}
              <Button onClick={() => setStep('form')} className="modern-btn mt-4">Zurück zum Formular</Button>
            </Col>
          </Row>
        </Container>
      )}
    </div>
  );
};

export default Dashboard;